import { useEffect } from "react";

const SITE_URL = "https://purposeful.inc";
const DEFAULT_IMAGE = `${SITE_URL}/assets/og-cover.jpg`;

function upsertMeta(attr, name, content) {
    if (content == null) return;
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href) {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function clearPageSchema() {
    document
        .querySelectorAll('script[type="application/ld+json"][data-page-schema="true"]')
        .forEach((n) => n.remove());
}

function addSchema(obj) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-page-schema", "true");
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
}

function breadcrumbSchema(crumbs) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: `${SITE_URL}${c.path}`,
        })),
    };
}

export default function SEO({
    title,
    description,
    path = "",
    keywords,
    image,
    type = "website",
    schema,
    breadcrumbs,
}) {
    useEffect(() => {
        const url = `${SITE_URL}${path}`;
        const img = image || DEFAULT_IMAGE;

        if (title) document.title = title;
        upsertMeta("name", "description", description);
        if (keywords) upsertMeta("name", "keywords", keywords);
        upsertMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1");

        // Open Graph
        upsertMeta("property", "og:title", title);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:url", url);
        upsertMeta("property", "og:type", type);
        upsertMeta("property", "og:image", img);
        upsertMeta("property", "og:site_name", "Purposeful, Inc.");

        // Twitter
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", title);
        upsertMeta("name", "twitter:description", description);
        upsertMeta("name", "twitter:image", img);

        upsertLink("canonical", url);

        // Structured data
        clearPageSchema();
        if (breadcrumbs && breadcrumbs.length) addSchema(breadcrumbSchema(breadcrumbs));
        if (schema) {
            const nowIso = new Date().toISOString();
            const withDates = (s) => {
                const t = s["@type"];
                if (["WebPage", "CollectionPage", "Blog", "ContactPage"].includes(t)) {
                    return { dateModified: nowIso, datePublished: s.datePublished || nowIso, ...s };
                }
                return s;
            };
            (Array.isArray(schema) ? schema : [schema]).map(withDates).forEach(addSchema);
        }

        return () => clearPageSchema();
    }, [title, description, path, keywords, image, type, JSON.stringify(schema), JSON.stringify(breadcrumbs)]);

    return null;
}
